/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

"use client";
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from "react";
import {
	AnimatePresence,
	type AnimatePresenceProps,
	motion,
	type MotionProps,
	type Transition,
} from "motion/react";
import { cn } from "../../lib/utils";

interface WordListSwapProps {
	texts: string[];
	rotationInterval?: number;
	initial?: MotionProps["initial"];
	animate?: MotionProps["animate"];
	exit?: MotionProps["exit"];
	animatePresenceMode?: AnimatePresenceProps["mode"];
	animatePresenceInitial?: boolean;
	staggerDuration?: number;
	staggerFrom?: "first" | "last" | "center" | number | "random";
	transition?: Transition;
	loop?: boolean; // Whether to start from the first text when the last one is reached
	auto?: boolean; // Whether to start the animation automatically
	splitBy?: "words" | "characters" | "lines";
	onNext?: (index: number) => void;
	/**
	 * Keep each text on one line instead of letting its words wrap. Only safe
	 * where the type is sized to fit the longest text in `texts` (see
	 * `useFitToWidth`); otherwise the text simply overflows.
	 */
	nowrap?: boolean;
	mainClassName?: string;
	splitLevelClassName?: string;
	elementLevelClassName?: string;
}

export interface WordListSwapRef {
	next: () => void;
	previous: () => void;
	jumpTo: (index: number) => void;
	reset: () => void;
}

interface WordObject {
	characters: string[];
	needsSpace: boolean;
}

const WordListSwap = forwardRef<WordListSwapRef, WordListSwapProps>(
	(
		{
			texts,
			transition = { type: "spring", damping: 25, stiffness: 300 },
			initial = { y: "100%", opacity: 0 },
			animate = { y: 0, opacity: 1 },
			exit = { y: "-120%", opacity: 0 },
			animatePresenceMode = "wait",
			animatePresenceInitial = false,
			rotationInterval = 2000,
			staggerDuration = 0,
			staggerFrom = "first",
			loop = true,
			auto = true,
			splitBy = "characters",
			onNext,
			nowrap = false,
			mainClassName,
			splitLevelClassName,
			elementLevelClassName,
			...props
		},
		ref,
	) => {
		const [currentTextIndex, setCurrentTextIndex] = useState(0);

		const elements = useMemo(() => {
			const currentText = texts[currentTextIndex];
			if (splitBy === "characters") {
				const text = currentText.split(" ");
				return text.map((word, i) => ({
					characters: Array.from(word),
					needsSpace: i !== text.length - 1,
				}));
			}
			if (splitBy === "words") {
				return currentText.split(" ");
			}
			if (splitBy === "lines") {
				return currentText.split("\n");
			}

			return currentText.split(splitBy);
		}, [texts, currentTextIndex, splitBy]);

		const getStaggerDelay = useCallback(
			(index: number, totalChars: number) => {
				const total = totalChars;
				if (staggerFrom === "first") {
					return index * staggerDuration;
				}
				if (staggerFrom === "last") {
					return (total - 1 - index) * staggerDuration;
				}
				if (staggerFrom === "center") {
					const center = Math.floor(total / 2);
					return Math.abs(center - index) * staggerDuration;
				}
				if (staggerFrom === "random") {
					const randomIndex = Math.floor(Math.random() * total);
					return Math.abs(randomIndex - index) * staggerDuration;
				}
				return Math.abs(staggerFrom - index) * staggerDuration;
			},
			[staggerFrom, staggerDuration],
		);

		// Helper function to handle index changes and trigger callback
		const handleIndexChange = useCallback(
			(newIndex: number) => {
				setCurrentTextIndex(newIndex);
				onNext?.(newIndex);
			},
			[onNext],
		);

		const next = useCallback(() => {
			let nextIndex: number;
			if (currentTextIndex === texts.length - 1) {
				nextIndex = loop ? 0 : currentTextIndex;
			} else {
				nextIndex = currentTextIndex + 1;
			}

			if (nextIndex !== currentTextIndex) {
				handleIndexChange(nextIndex);
			}
		}, [currentTextIndex, texts.length, loop, handleIndexChange]);

		const previous = useCallback(() => {
			let prevIndex: number;
			if (currentTextIndex === 0) {
				prevIndex = loop ? texts.length - 1 : currentTextIndex;
			} else {
				prevIndex = currentTextIndex - 1;
			}

			if (prevIndex !== currentTextIndex) {
				handleIndexChange(prevIndex);
			}
		}, [currentTextIndex, texts.length, loop, handleIndexChange]);

		const jumpTo = useCallback(
			(index: number) => {
				const validIndex = Math.max(0, Math.min(index, texts.length - 1));
				if (validIndex !== currentTextIndex) {
					handleIndexChange(validIndex);
				}
			},
			[texts.length, currentTextIndex, handleIndexChange],
		);

		const reset = useCallback(() => {
			if (currentTextIndex !== 0) {
				handleIndexChange(0);
			}
		}, [currentTextIndex, handleIndexChange]);

		// Expose all navigation functions via ref
		useImperativeHandle(
			ref,
			() => ({
				next,
				previous,
				jumpTo,
				reset,
			}),
			[next, previous, jumpTo, reset],
		);

		useEffect(() => {
			if (!auto) {
				return;
			}
			const intervalId = setInterval(next, rotationInterval);
			return () => clearInterval(intervalId);
		}, [next, rotationInterval, auto]);

		return (
			<motion.span
				className={cn(
					"flex whitespace-pre-wrap",
					nowrap ? "flex-nowrap" : "flex-wrap",
					mainClassName,
				)}
				{...props}
				layout={true}
				transition={transition}>
				<span className="sr-only">{texts[currentTextIndex]}</span>

				<AnimatePresence
					mode={animatePresenceMode}
					initial={animatePresenceInitial}>
					<motion.span
						key={currentTextIndex}
						className={cn(
							"flex",
							nowrap ? "flex-nowrap" : "flex-wrap",
							splitBy === "lines" && "flex-col w-full",
						)}
						layout={true}
						aria-hidden="true">
						{(splitBy === "characters" ?
							(elements as WordObject[])
						:	(elements as string[]).map((el, i) => ({
								characters: [el],
								needsSpace: i !== elements.length - 1,
							}))
						).map((wordObj, wordIndex, array) => {
							const previousCharsCount = array
								.slice(0, wordIndex)
								.reduce((sum, word) => sum + word.characters.length, 0);

							return (
								<span
									key={`${wordIndex}-${wordObj.characters.join("")}`}
									className={cn(
										"inline-flex",
										// See BlurText: without this a nowrap line shrinks its
										// words and breaks them mid-word instead of overflowing.
										nowrap && "shrink-0",
										splitLevelClassName,
									)}>
									{wordObj.characters.map((char, charIndex) => (
										<motion.span
											initial={initial}
											animate={animate}
											exit={exit}
											key={`${charIndex}-${char}`}
											transition={{
												...transition,
												delay: getStaggerDelay(
													previousCharsCount + charIndex,
													array.reduce(
														(sum, word) => sum + word.characters.length,
														0,
													),
												),
											}}
											className={cn(
												"inline-block",
												nowrap && "shrink-0",
												elementLevelClassName,
											)}>
											{char}
										</motion.span>
									))}
									{wordObj.needsSpace && (
										<span className="whitespace-pre"> </span>
									)}
								</span>
							);
						})}
					</motion.span>
				</AnimatePresence>
			</motion.span>
		);
	},
);

export default WordListSwap;
